import ReorderIcon from "@mui/icons-material/Reorder";
import {Autocomplete, Box, TextField, Typography} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {AggregationServiceListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import {CommonAggregationService} from "crqlar-react-common/dist/services/aggregation/commonAggregation.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useAllServiceCategories} from "crqlar-react-common/dist/utils/hooks/services/useAllServiceCategories";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    ServerTableToolbarAction,
    useServerTable
} from "../../common/serverTable";
import ReorderScreen from "../common/ReorderScreen";
import SettingsListPage from "../layout/SettingsListPage";

const ServicesList = () => {
    useTrackScreenVisit('system services');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const {localize} = useLocalize();
    const {hasGuestAppModule} = useHasGuestAppModule();

    const [reorderMode, setReorderMode] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
    const [loadingServices, setLoadingServices] = useState(false);
    const allCategories = useAllServiceCategories();

    const columns = useMemo<ColumnDef<AggregationServiceListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: service => service.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 260,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'categories',
            header: t('Categories'),
            accessorFn: service => service.categories.map(category => category.name).filter(Boolean),
            cell: ({row}) => (
                <Box>{row.original.categories.map(category => category.name).filter(Boolean).join(', ')}</Box>
            ),
            size: 220,
            meta: {defaultActive: true}
        },
        {
            id: 'sectors',
            header: t('Sectors'),
            accessorFn: service => service.sectors.map(sector => sector.name).filter(Boolean),
            cell: ({row}) => (
                <Box>{row.original.sectors.map(sector => sector.name).filter(Boolean).join(', ')}</Box>
            ),
            size: 220,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'sectorName'}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: service => service.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: service => service.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const filterOptionsQuery = useQuery(
        ['server-table-service-filter-options'],
        () => ServiceService.getFilterOptions(),
        {staleTime: Infinity}
    );

    const controller = useServerTable({
        tableId: 'system-services',
        columns,
        getRowId: service => service.id,
        onRowClick: service => navigate(`/system/services/${service.id}`),
        initialSorting: [{id: 'sectors', desc: false}],
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => CommonAggregationService.listServices({
            name: searchQuery ? [searchQuery] : undefined,
            advancedFilter: filterRules.length ? {
                filterGroups: [{filterRules, operator: DataFilterOperator.AND}]
            } : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        })
    });

    if (reorderMode) {
        return (
            <ReorderScreen<Service>
                title={t('Reorder Services')}
                hint={t('Drag services to set the display order in the guest app.')}
                getLabel={service => localize(service.nameLocalized) || service.name || '-'}
                save={service => ServiceService.updateService(service)}
                busy={loadingServices}
                onClose={() => {
                    setReorderMode(false);
                    setSelectedCategory(null);
                    void controller.query.refetch();
                }}
                emptyState={
                    <Typography variant={'caption'} color={'text.secondary'}>
                        {selectedCategory
                            ? t('This category has no services yet.')
                            : t('Select a category to reorder its services')}
                    </Typography>
                }
                controls={({dirty, setItems}) => (
                    <Autocomplete<ServiceCategory>
                        id={'reorder-category'}
                        options={allCategories}
                        getOptionLabel={(option: ServiceCategory) => option.name || ''}
                        value={selectedCategory}
                        disabled={dirty || loadingServices}
                        renderInput={(params) => (
                            <TextField {...params} size={'small'} label={t('Category')} fullWidth/>
                        )}
                        onChange={async (_e, value) => {
                            setSelectedCategory(value);
                            if (!value?.id) {
                                setItems([]);
                                return;
                            }
                            setLoadingServices(true);
                            try {
                                const response = await ServiceService.getAllServices({
                                    categoryIds: [value.id],
                                    limit: {offset: 0, limit: 1000, sort: [{field: 'orderPriority', direction: 'ASC'}]}
                                });
                                setItems(response.content || []);
                            } finally {
                                setLoadingServices(false);
                            }
                        }}
                        fullWidth
                    />
                )}
            />
        );
    }

    return (
        <SettingsListPage title={t('Services')} back={() => navigate('/system')}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <ServerTableToolbar hoistOnCompact>
                            {hasGuestAppModule && (
                                <ServerTableToolbarAction
                                    align="start"
                                    variant="text"
                                    startIcon={<ReorderIcon/>}
                                    onClick={() => setReorderMode(true)}
                                >
                                    {t('Reorder')}
                                </ServerTableToolbarAction>
                            )}
                            <ServerTableSearch/>
                            <ServerTableSort/>
                            <ServerTableFilter
                                options={filterOptionsQuery.data ?? []}
                                disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                            />
                            <ServerTableFullscreen/>
                            <ServerTableColumns/>
                            <ServerTableCreate
                                label={t('Create Service')}
                                onClick={() => navigate('/system/services/new')}
                            />
                        </ServerTableToolbar>

                        <ServerTable/>
                    </ServerTableFrame>
                </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ServicesList;
